import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Landmark, Hotel, Quote } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function Projects() {
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'International Banking HQ',
      category: 'banking',
      location: 'London, UK',
      year: '2023',
      description: 'Complete vault and access control system for a tier-1 banking headquarters.',
      image: 'https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&q=85',
      solution: 'Vault Systems + Biometric Access',
      testimonial: 'Green Flux delivered exceptional security infrastructure on time and within budget. Their attention to detail is unmatched.',
      client: 'Head of Security, Major Bank',
    },
    {
      id: 2,
      title: 'Federal Government Complex',
      category: 'government',
      location: 'New York, USA',
      year: '2022',
      description: 'Multi-building security upgrade with blast-resistant doors and centralized access control.',
      image: 'https://images.unsplash.com/photo-1705776919715-fa052f797923?w=800&q=85',
      solution: 'Reinforced Doors + Access Control',
      testimonial: 'The team understood our stringent requirements and delivered a system that exceeded all compliance standards.',
      client: 'Facility Director, Federal Agency',
    },
    {
      id: 3,
      title: 'Luxury Hotel Chain',
      category: 'hospitality',
      location: 'London, UK',
      year: '2023',
      description: 'Guest safety and asset protection system for a premium hospitality brand.',
      image: 'https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?w=800&q=85',
      solution: 'Fire-Resistant Doors + Smart Locks',
      testimonial: 'Security that doesn\'t compromise aesthetics. The installation was seamless and the results are outstanding.',
      client: 'General Manager, Boutique Hotels',
    },
    {
      id: 4,
      title: 'Embassy Compound',
      category: 'government',
      location: 'New York, USA',
      year: '2021',
      description: 'Comprehensive perimeter and access security for a diplomatic mission.',
      image: 'https://images.unsplash.com/photo-1668609537021-f377356358f9?w=800&q=85',
      solution: 'Reinforced Doors + Biometric Access',
      testimonial: 'Professionalism and discretion throughout. Green Flux is now our preferred security partner.',
      client: 'Security Attaché',
    },
    {
      id: 5,
      title: 'Regional Bank Network',
      category: 'banking',
      location: 'UK-wide',
      year: '2022',
      description: 'Standardized security system rollout across 15 branch locations.',
      image: 'https://images.pexels.com/photos/965878/pexels-photo-965878.jpeg?w=800&q=85',
      solution: 'Vault Systems + Maintenance',
      testimonial: 'Coordinating across multiple sites was handled flawlessly. Every branch is now secure and compliant.',
      client: 'Operations Director, Regional Bank',
    },
    {
      id: 6,
      title: 'Corporate Headquarters',
      category: 'hospitality',
      location: 'New York, USA',
      year: '2023',
      description: 'Executive floor security and server room protection for a Fortune 500 company.',
      image: 'https://images.pexels.com/photos/64609/pexels-photo-64609.jpeg?w=800&q=85',
      solution: 'Access Control + Fire-Resistant',
      testimonial: 'The integration with our existing systems was seamless. Highly recommend their technical expertise.',
      client: 'CTO, Technology Corporation',
    },
  ];

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'banking', label: 'Banking' },
    { value: 'government', label: 'Government' },
    { value: 'hospitality', label: 'Hospitality' },
  ];

  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.category === filter);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <section className="py-16 bg-card border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="text-accent border-accent mb-4">
              Case Studies
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-semibold mb-6">Proven Track Record</h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our portfolio of successful installations across banking, government, and hospitality sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-background border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredProjects.length} {filter === 'all' ? '' : filter} projects
            </p>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]" data-testid="projects-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group overflow-hidden h-full flex flex-col" data-testid="case-study-card">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-primary text-primary-foreground">
                        {project.category === 'banking' && <Landmark className="h-3 w-3 mr-1" />}
                        {project.category === 'government' && <Building2 className="h-3 w-3 mr-1" />}
                        {project.category === 'hospitality' && <Hotel className="h-3 w-3 mr-1" />}
                        {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="flex-grow">
                    <CardTitle className="text-xl">{project.title}</CardTitle>
                    <CardDescription>
                      <div className="space-y-1 text-xs">
                        <p>{project.location} • {project.year}</p>
                        <p className="text-accent font-medium">{project.solution}</p>
                      </div>
                    </CardDescription>
                    <p className="text-sm text-muted-foreground mt-2">{project.description}</p>
                  </CardHeader>

                  <CardContent className="border-t border-border pt-4">
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Quote className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-muted-foreground italic">{project.testimonial}</p>
                      </div>
                      <p className="text-xs font-medium text-right">— {project.client}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
